import {
  AddUpdateTaskParams,
  dateFromDateString,
  EventByDay,
  Frequency,
  toDateString,
} from '@nextforce/api'
import { Divider, Icon, Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FlatList, View } from 'react-native'
import {
  Dropdown,
  DropdownItem,
  DropdownItemComponentProps,
  DropdownSection,
} from 'shared/components/dropdown'
import { Collapsible } from 'shared/components/list/collapsible'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import {
  ModalWheelPicker,
  ModalWheelPickerItem,
} from 'shared/components/modal_wheel_picker'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { TaskViewItem } from 'tasks/screens/edit_task/items'
import { getDefaultEndDateForFrequency, sortByDays } from 'tasks/utils'

type FrequencyItem = {
  value: Frequency
  label: string
}

type ByDayItem = {
  value: EventByDay
}

interface Props {
  taskParams: AddUpdateTaskParams
  setTaskParams: React.Dispatch<React.SetStateAction<AddUpdateTaskParams>>
}

export function EditRecurrentTaskView({ taskParams, setTaskParams }: Props) {
  const styles = useStyles()
  const minDueDate = moment().startOf('day').add(1, 'day').toDate()

  const monthDateList: ModalWheelPickerItem<number>[] = Array.from(
    { length: 31 },
    (_, i) => ({
      value: i + 1,
      label: (i + 1).toString(),
    })
  )

  const [isMonthlyOnPickerVisible, setMonthlyOnPickerVisible] = useState(false)

  // Update end date when frequency, interval, or due date changes
  useEffect(() => {
    if (taskParams.frequency && taskParams.dueDate) {
      const newEndDate = getDefaultEndDateForFrequency(
        taskParams.frequency,
        taskParams.dueDate,
        taskParams.interval || 1
      )
      setTaskParams(prev => ({ ...prev, until: newEndDate }))
    }
  }, [taskParams.frequency, taskParams.dueDate, taskParams.interval, setTaskParams])

  const items = useMemo(() => {
    const byDayItem: TaskViewItem = {
      type: 'byDay',
      key: 'byDay',
    }
    const monthlyOnItem: TaskViewItem = {
      type: 'monthlyOn',
      key: 'monthlyOn',
    }
    const untilItem: TaskViewItem = {
      type: 'until',
      key: 'until',
    }
    const dueDateItem: TaskViewItem = {
      type: 'dueDate',
      key: 'dueDate',
    }
    const frequencyItem: TaskViewItem = {
      type: 'frequency',
      key: 'frequency',
    }

    const recurringItems: TaskViewItem[] = [frequencyItem]

    switch (taskParams.frequency) {
      case 'DAILY':
        break
      case 'WEEKLY':
        recurringItems.push(byDayItem)
        break
      case 'MONTHLY':
        recurringItems.push(monthlyOnItem)
        break
      case 'YEARLY':
        break
      default:
        break
    }
    recurringItems.push(dueDateItem, untilItem)

    return recurringItems
  }, [taskParams.frequency])

  const onDueDateChange = useCallback(
    (date: Date) => {
      setTaskParams(prev => ({ ...prev, dueDate: toDateString(date) }))
    },
    [setTaskParams]
  )
  const onUntilChange = useCallback(
    (date: Date) => {
      setTaskParams(prev => ({ ...prev, until: toDateString(date) }))
    },
    [setTaskParams]
  )

  const renderItem = useCallback(
    ({ item }: { item: TaskViewItem }) => {
      switch (item?.type) {
        case 'dueDate':
          return (
            <>
              <Divider />
              <DateTimeChooser
                title={'Starts'}
                value={
                  taskParams.dueDate
                    ? dateFromDateString(taskParams.dueDate)
                    : undefined
                }
                mode="date"
                onChange={onDueDateChange}
                minDate={minDueDate}
              />
            </>
          )
        case 'until':
          return (
            <DateTimeChooser
              title={'Ends'}
              value={
                taskParams.until
                  ? dateFromDateString(taskParams.until)
                  : undefined
              }
              mode="date"
              onChange={onUntilChange}
              minDate={
                taskParams.dueDate
                  ? moment(taskParams.dueDate).add(1, 'day').toDate()
                  : minDueDate
              }
            />
          )
        case 'monthlyOn':
          return (
            <Collapsible
              title={t('onDay')}
              value={
                taskParams.monthlyOn
                  ? taskParams.monthlyOn.toString()
                  : t('none')
              }
              expanded={isMonthlyOnPickerVisible}
              onPress={() =>
                setMonthlyOnPickerVisible(!isMonthlyOnPickerVisible)
              }
              icon={null}
            >
              <ModalWheelPicker
                isVisible={isMonthlyOnPickerVisible}
                title={t('onDay')}
                itemList={monthDateList}
                onSelect={monthlyOn =>
                  setTaskParams(prev => ({ ...prev, monthlyOn }))
                }
                onClose={() => setMonthlyOnPickerVisible(false)}
                selectedItem={taskParams.monthlyOn}
              />
            </Collapsible>
          )
        case 'frequency':
          return (
            <FrequencyChooser
              value={taskParams.frequency}
              setFrequency={frequency =>
                setTaskParams(prev => ({ ...prev, frequency }))
              }
              interval={taskParams.interval}
              setInterval={interval =>
                setTaskParams(prev => ({ ...prev, interval }))
              }
            />
          )
        case 'byDay':
          return (
            <ByDayChooser
              value={taskParams.byDay}
              setByDay={byDay => setTaskParams(prev => ({ ...prev, byDay }))}
            />
          )
        default:
          return null
      }
    },
    [
      taskParams.dueDate,
      taskParams.until,
      taskParams.monthlyOn,
      taskParams.frequency,
      taskParams.interval,
      taskParams.byDay,
      onDueDateChange,
      minDueDate,
      onUntilChange,
      isMonthlyOnPickerVisible,
      monthDateList,
      setTaskParams,
    ]
  )

  return (
    <FlatList
      style={styles.container}
      data={items}
      renderItem={renderItem}
      contentContainerStyle={styles.contentContainer}
    />
  )
}

export function FrequencyChooser({
  value,

  setFrequency,
  interval,
  setInterval,
}: {
  value: Frequency | undefined

  setFrequency: (frequency: Frequency | undefined) => void
  interval: number | undefined
  setInterval: (interval: number | undefined) => void
}) {
  const styles = useStyles()
  const items: FrequencyItem[] = useMemo(
    () => [
      {
        value: 'DAILY',
        label:
          interval === 1 || interval === undefined ? t('day') : t('freqDays'),
      },
      {
        value: 'WEEKLY',
        label:
          interval === 1 || interval === undefined ? t('week') : t('freqWeeks'),
      },
      {
        value: 'MONTHLY',
        label:
          interval === 1 || interval === undefined
            ? t('month')
            : t('freqMonths'),
      },
      {
        value: 'YEARLY',
        label:
          interval === 1 || interval === undefined ? t('year') : t('freqYears'),
      },
    ],
    [interval]
  )

  const valueLabel = items.find(item => item.value === value)?.label

  const toggle = useCallback(
    (item: Frequency | undefined) => {
      setFrequency(item)
    },
    [setFrequency]
  )
  const frequencySections: DropdownSection<DropdownItem>[] = items.map(item => {
    return { data: [{ id: item.value, name: item.label }] }
  })

  const onChangeIntervalText = useCallback(
    (text: string) => {
      // Only accept numeric values and reject zero
      const numValue = Number(text)
      if (!isNaN(numValue)) {
        if (numValue === 0) {
          setInterval(undefined)
        } else {
          setInterval(numValue)
        }
      } else if (text === '') {
        setInterval(undefined)
      }
    },
    [setInterval]
  )

  return (
    <View style={styles.frequencyChooserContainer}>
      <Text style={styles.frequencyChooserTitle}>Repeat every</Text>
      <View style={styles.frequencyInputRow}>
        <View style={styles.intervalContainer}>
          <Input
            value={interval?.toString() || ''}
            onChangeText={onChangeIntervalText}
            containerStyle={styles.intervalInputContainer}
            style={styles.intervalInput}
            keyboardType="numeric"
            maxLength={2}
            numberOfLines={1}
          />
        </View>
        <View style={styles.frequencyDropdownContainer}>
          <Dropdown
            sections={frequencySections}
            selectedId={value}
            onSelect={toggle}
            title={valueLabel}
            ItemComponent={FrequencyChooserItemComponent}
            buttonStyles={styles.frequencyDropdownButton}
            titleStyle={styles.frequencyDropdownTitle}
          />
        </View>
      </View>
    </View>
  )
}

function FrequencyChooserItemComponent(props: DropdownItemComponentProps) {
  const styles = useStyles()
  return (
    <View style={styles.frequencyDropdownItem}>
      <View style={styles.frequencyDropdownItemText}>
        <Text>{props.item.name}</Text>
      </View>
      {props.selected ? <Icon name="check" /> : null}
    </View>
  )
}

const BY_DAY_ITEMS: ByDayItem[] = [
  { value: 'MO' },
  { value: 'TU' },
  { value: 'WE' },
  { value: 'TH' },
  { value: 'FR' },
  { value: 'SA' },
  { value: 'SU' },
]

export function ByDayChooser({
  value,
  setByDay,
}: {
  value: EventByDay[] | undefined
  setByDay: (byDay: EventByDay[] | undefined) => void
}) {
  const styles = useStyles()

  const onToggleDays = useCallback(
    (selectedDay: EventByDay | undefined) => {
      const newSelectedDays =
        selectedDay === undefined
          ? selectedDay
          : value
          ? value?.some(s => s === selectedDay)
            ? value?.filter(s => s !== selectedDay)
            : [...value, selectedDay]
          : [selectedDay]
      // Sort days based on day of week order
      const sortedNewSelectedDays = newSelectedDays
        ? sortByDays(newSelectedDays)
        : undefined

      setByDay(sortedNewSelectedDays)
    },
    [value, setByDay]
  )

  return (
    <View style={styles.byDayChooserContainer}>
      {BY_DAY_ITEMS.map((item, index) => (
        <PressableOpacity key={index} onPress={() => onToggleDays(item.value)}>
          <View
            style={[
              styles.byDayItem,
              {
                backgroundColor: value?.includes(item.value)
                  ? styles.checkboxChecked.color
                  : styles.checkboxUnchecked.color,
              },
            ]}
          >
            <Text>{item.value}</Text>
          </View>
        </PressableOpacity>
      ))}
    </View>
  )
}
export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.white,
    marginTop: 10,
  },

  checkboxChecked: {
    color: theme.colors?.primary,
  },
  checkboxUnchecked: {
    color: theme.colors?.grey5,
  },
  contentContainer: { flexGrow: 1 },
  frequencyChooserContainer: { paddingHorizontal: 20, marginBottom: 10 },
  frequencyChooserTitle: { fontSize: 16, marginBottom: 10 },
  frequencyInputRow: { flexDirection: 'row' },
  intervalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 50,
  },
  intervalInputContainer: {
    padding: 10,
    borderColor: theme.colors.grey3,
    borderWidth: 1,
    borderRadius: 15,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  intervalInput: { textAlign: 'center', width: 30 },
  frequencyDropdownContainer: {
    flex: 1,
    justifyContent: 'center',
    marginVertical: 10,
    marginLeft: 10,
  },
  frequencyDropdownButton: {
    borderWidth: 1,
    borderColor: theme.colors.grey3,
    padding: 15,
    borderRadius: 15,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  frequencyDropdownTitle: {
    color: theme.colors.black,
    flex: 1,
    textAlign: 'left',
  },
  frequencyDropdownItem: {
    padding: 10,
    width: 200,
    alignItems: 'center',
    flexDirection: 'row',
  },
  frequencyDropdownItemText: { flex: 1 },
  byDayChooserContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: 20,
    marginBottom: 10,
  },
  byDayItem: {
    padding: 15,
    margin: 5,
    borderRadius: 50,
  },
}))
